import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { FaYoutube, FaRedditAlien, FaXTwitter } from 'react-icons/fa6';
import ResearchPipelinePage from './ResearchPipelinePage';
import XPipelinePage from './XPipelinePage';
import RedditPipelinePage from './RedditPipelinePage';
import CollectorsPanel from './CollectorsPanel';
import { useAuth } from '../../context/AuthContext';

// Chatter — the multi-source public-signal ingest. The landing is now just the SOURCE PICKER
// (YouTube + X + Reddit live). The summary dashboard (stats / latest takeaways /
// most-discussed) moved INTO the YouTube tab (ChatterDashboard, rendered by ResearchPipelinePage)
// so each source owns its own summary. The pipeline behind YouTube is unchanged.
const PLATFORMS = [
  { key: 'youtube', label: 'YouTube', Icon: FaYoutube, color: '#FF0000',
    live: true, desc: 'Podcasts & interviews — transcript ingest → extraction → judgment.' },
  { key: 'reddit', label: 'Reddit', Icon: FaRedditAlien, color: '#FF4500',
    live: true, desc: 'Tracked subreddits & searches — read nightly through the Mac mini, one document per source-day, same extraction.' },
  { key: 'x', label: 'X', Icon: FaXTwitter, cls: 'text-gray-900 dark:text-gray-100',
    live: true, desc: 'Curated handles — original posts & quotes, one document per handle-day, same extraction.' },
];

function ChatterHome() {
  const nav = useNavigate();
  const { user } = useAuth();
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="font-serif text-xl text-gray-900 dark:text-gray-100">Chatter</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Public-signal ingest across social sources — pick a source to see what we track, extract, and learn.
        </p>
        {user?.is_admin && (
          <button type="button" onClick={() => nav('/researchos/chatter-new')}
            className="mt-2 text-xs font-medium text-brand-gold hover:underline">Try the new Chatter view →</button>
        )}
      </div>

      {/* ── platform tiles (the source picker) ── */}
      <div>
        <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">Sources</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PLATFORMS.map((p) => (
            <button key={p.key} disabled={!p.live}
              onClick={() => p.live && nav(`/researchos/chatter/${p.key}`)}
              className={`group flex flex-col gap-3 rounded-xl border border-gray-200 p-4 text-left transition-all dark:border-gray-800 ${p.live ? 'hover:border-brand-gold hover:shadow-sm' : 'cursor-not-allowed opacity-60'}`}>
              <div className="flex items-center justify-between">
                <span className={`grid h-11 w-11 place-items-center rounded-lg bg-gray-100 dark:bg-gray-800/60 ${p.cls || ''}`}
                  style={p.color ? { color: p.color } : undefined}>
                  <p.Icon size={26} />
                </span>
                {p.live
                  ? <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-green-500">● Live</span>
                  : <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-gray-400">Soon</span>}
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">{p.label}</div>
                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{p.desc}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ── collectors heartbeat (wish #22) ── */}
      <CollectorsPanel />
    </div>
  );
}

export default function ChatterPage() {
  return (
    <Routes>
      <Route index element={<ChatterHome />} />
      <Route path="youtube/*" element={<ResearchPipelinePage />} />
      <Route path="x/*" element={<XPipelinePage />} />
      <Route path="reddit/*" element={<RedditPipelinePage />} />
      <Route path="*" element={<Navigate to="/researchos/chatter" replace />} />
    </Routes>
  );
}
